/**
 * Pure helpers for the Teams tab of the Team page (no React, no IO).
 */
import { isRoleBelow, teamErrorText, type RankRole } from './team-rank';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Is the Delete team control offered? To an owner always; to a manager only
 * for a team without a role or with a role below manager, which is the rule
 * the API applies. No other role deletes a team.
 */
export function canDeleteTeam(
  callerRole: string | null | undefined,
  team: { role?: string | null },
  roles: RankRole[] = []
): boolean {
  if (callerRole === 'owner') return true;
  if (callerRole !== 'manager') return false;
  return !team.role || isRoleBelow(callerRole, team.role, roles);
}

/** May the create-team form be sent? A team needs a name and a lead. */
export function teamFormReady(name: string, leadId: string): boolean {
  return name.trim() !== '' && leadId !== '';
}

/**
 * The sentence a team dialog shows for a refused request: the API's own
 * `reason` or validation message, and plain words for the one refusal that
 * comes back as a bare code.
 */
export function teamDialogErrorText(
  err: unknown,
  t: Translate,
  fallback: string
): string {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  if (code === 'team_lead_not_in_organisation') {
    return t('TeamLeadNotInOrganisation');
  }
  return teamErrorText(err, fallback);
}

/** The parts of a team and of a member row the lead's name is read from. */
interface LeadSource {
  team_lead?: { name?: string | null; email?: string | null } | null;
  team_lead_name?: string | null;
  team_lead_id?: string | null;
}

/**
 * The name of a team's lead, or null when the team has none. The list sends
 * the lead as `team_lead`; a team that carries only `team_lead_id` (the answer
 * to a create) is looked up among the loaded members.
 */
export function teamLeadName(
  team: LeadSource,
  members: { id: string; name?: string | null; email?: string | null }[] = []
): string | null {
  const direct =
    team.team_lead?.name || team.team_lead?.email || team.team_lead_name;
  if (direct) return direct;
  if (!team.team_lead_id) return null;
  const member = members.find((m) => m.id === team.team_lead_id);
  return member?.name || member?.email || null;
}
